import * as ExcelJS from 'exceljs';
import * as FileSaver from 'file-saver';
import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { API_ENDPOINTS } from '@app/shared/constants/apiconstants';
import { AccordianComponent } from '@app/shared/custom-component/accordian/accordian.component';
import { DataGridComponent } from '@app/shared/custom-component/data-grid/data-grid.component';
import { DataGridVO } from '@app/shared/custom-component/data-grid/data-grid.model';
import { DateMaskDirective } from '@app/shared/directives/dateMask.directive';
import { HttpService } from '@core/services/http.service';
import { AlertService } from '@app/@core/services/alert.service';
import { take } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';

interface EPPReport {
  title: string;
  school_year: string | null;
  progress: number;
  status: string;
  updatedDate: string | null;
  showDownload: boolean;
}

@Component({
  selector: 'app-epp-reports',
  standalone: true,
  imports: [CommonModule, FormsModule, AccordianComponent, DataGridComponent, MatTooltipModule, DateMaskDirective],
  templateUrl: './epp-reports.component.html',
  styleUrls: ['./epp-reports.component.scss']
})
export class EPPReportsComponent implements OnInit, AfterViewInit {
  @ViewChild(AccordianComponent) accordion?: AccordianComponent;
  @ViewChild(DataGridComponent) reportGrid?: DataGridComponent;

  constructor(private cdr: ChangeDetectorRef, private httpService: HttpService, private alertService: AlertService) {}

  eppReportData: EPPReport[] = [];
  private rawReportData: any[] = [];
  showGenerateReportModal = false;
  reportHoverIndex: number | null = null;
  reportStartDate = '';
  reportEndDate = '';
  readonly todayIsoDate = this.getTodayIsoDate();
  dateRangeError = '';
  startDateMaskError = false;
  endDateMaskError = false;
  private selectedReportRow: any = null;

  eppReportGridConfig: DataGridVO = new DataGridVO({
    role: 'reports',
    activeTab: 'epp-reports',
    customHeaders: [
      { dataField: 'title', caption: 'Title', allowFiltering: true, allowSearch: { isSearchEnable: true, type: 'string' }, cssClass: 'source-wrap-column' },
      { dataField: 'updatedDate', caption: 'Last Updated Date', allowFiltering: true, allowSearch: { isSearchEnable: true, type: 'date' }, dataType: 'date' }
    ],
    showActionsColumn: true,
    showQueueActions: false,
    showFiltering: true,
    showSearchRow: true,
    showPaging: true,
    customPageSize: 10,
    customPageSizes: [10, 25, 50],
    customNoDataText: 'No reports found',
    data: this.eppReportData
  });

  ngOnInit(): void {
    this.fetchReportData();
  }

  private fetchReportData(): void {
    this.httpService.post(API_ENDPOINTS.REPORTS, { tab: 'EPP' }).pipe(take(1)).subscribe({
      next: (response: any) => {
        const reportData = Array.isArray(response?.data?.data) ? response.data.data : Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
        this.rawReportData = reportData;
        this.eppReportData = reportData.map((item: any, index: number) => ({
          ...item,
          _index: index,
          title: item.title ?? '---',
          school_year: item.school_year ? item.school_year.split('-').map((y: string) => y.trim().slice(-2)).join('/') : '---',
          progress: item.progress != null ? `${item.progress}` : '---',
          status: item.status ?? '---',
          updatedDate: item.updatedDate ?? '---',
          showDownload: item.showDownload ?? false
        }));
        this.eppReportGridConfig = this.eppReportGridConfig.updateConfig({ data: this.eppReportData });
        this.cdr.markForCheck();
        setTimeout(() => this.disableCellHint());
      },
      error: (err: any) => {
        console.error('Failed to fetch EPP reports:', err);
      }
    });
  }

  openGenerateReportModal(rowData: any): void {
    this.selectedReportRow = rowData;
    this.resetGenerateReportFields();
    this.showGenerateReportModal = true;
  }

  closeGenerateReportModal(): void {
    this.showGenerateReportModal = false;
    this.selectedReportRow = null;
    this.resetGenerateReportFields();
  }

  resetGenerateReportFields(): void {
    this.reportStartDate = '';
    this.reportEndDate = '';
    this.dateRangeError = '';
    this.startDateMaskError = false;
    this.endDateMaskError = false;
  }

  onDateInputChanged(): void {
    this.validateDateRange();
  }

  onStartDateMaskError(hasError: boolean): void {
    this.startDateMaskError = hasError;
    this.validateDateRange();
  }

  onEndDateMaskError(hasError: boolean): void {
    this.endDateMaskError = hasError;
    this.validateDateRange();
  }

  onStartDatePickerChange(value: string): void {
    this.reportStartDate = this.formatDate(value);
    this.startDateMaskError = false;
    this.validateDateRange();
  }

  onEndDatePickerChange(value: string): void {
    this.reportEndDate = this.formatDate(value);
    this.endDateMaskError = false;
    this.validateDateRange();
  }

  toIsoDate(value: string): string {
    const parsedDate = this.parseDate(value);
    if (!parsedDate) {
      return '';
    }

    const month = `${parsedDate.getMonth() + 1}`.padStart(2, '0');
    const day = `${parsedDate.getDate()}`.padStart(2, '0');
    return `${parsedDate.getFullYear()}-${month}-${day}`;
  }

  confirmGenerateReportDownload(): void {
    this.validateDateRange();
    if (!this.canDownloadReport || !this.selectedReportRow) {
      return;
    }

    this.performDownloadReport(this.selectedReportRow);
    this.closeGenerateReportModal();
  }

  get canDownloadReport(): boolean {
    return !!this.reportStartDate && !!this.reportEndDate && !this.startDateMaskError && !this.endDateMaskError && !this.dateRangeError;
  }

  private validateDateRange(): void {
    this.dateRangeError = '';

    const startDate = this.parseDate(this.reportStartDate);
    const endDate = this.parseDate(this.reportEndDate);

    if (!startDate || !endDate) {
      return;
    }

    if (startDate.getTime() > endDate.getTime()) {
      this.dateRangeError = 'Cannot be greater than Start Date.';
    }
  }

  private getTodayIsoDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = `${today.getMonth() + 1}`.padStart(2, '0');
    const day = `${today.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatDate(dateString: string): string {
    if (!dateString) {
      return '';
    }

    const [year, month, day] = dateString.split('-');
    if (!year || !month || !day) {
      return '';
    }

    return `${month}/${day}/${year}`;
  }

  private parseDate(value: string): Date | null {
    if (!value || value.length !== 10) {
      return null;
    }

    const [month, day, year] = value.split('/').map((part) => Number(part));
    if (!month || !day || !year) {
      return null;
    }

    const parsedDate = new Date(year, month - 1, day);
    if (
      parsedDate.getFullYear() !== year ||
      parsedDate.getMonth() !== month - 1 ||
      parsedDate.getDate() !== day
    ) {
      return null;
    }

    parsedDate.setHours(0, 0, 0, 0);
    return parsedDate;
  }

  private performDownloadReport(rowData: any): void {
    const item = (rowData._index != null && this.rawReportData[rowData._index]) ? this.rawReportData[rowData._index] : rowData;
    const fileName = item.title || 'EPP';
    const url = API_ENDPOINTS.DATA_TEMPLATES.DOWNLOAD_TEMPLATES;
    const identity_id = item.id;
    const contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    
    // Capture dates here before the modal closes and resets them
    const startDate = this.parseDate(this.reportStartDate);
    const endDate = this.parseDate(this.reportEndDate);
    const isoStartDate = this.toIsoDate(this.reportStartDate);
    const isoEndDate = this.toIsoDate(this.reportEndDate);

    // Step 0: Call generate-report API to get the report data
    const generateReportPayload = {
      startDate: isoStartDate,
      endDate: isoEndDate,
      templateName: fileName + '.xlsx'
    };

    this.httpService.post(API_ENDPOINTS.GENERATE_REPORT, generateReportPayload).pipe(take(1)).subscribe({
      next: (response: any) => {
        const responseData = response;

        // Step 1: Download the template
        this.httpService.getDownload1(url + '/' + item.data_template_col_sel_ids + '/' + item.template_id + '/' + identity_id + '/' + false + '/' + true, contentType).subscribe({
          next: async (blob: Blob) => {
            const arrayBuffer = await blob.arrayBuffer();
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(arrayBuffer);

            const worksheet = workbook.getWorksheet(1);
            if (!worksheet) {
              console.error('No worksheet found in template');
              return;
            }

            let data: any[] = [];
            
            if (Array.isArray(responseData)) {
              data = responseData;
            } else if (responseData && typeof responseData === 'object') {
              if (responseData.data && typeof responseData.data === 'object' && Array.isArray(responseData.data.data)) {
                const dataArray = responseData.data.data;
                if (dataArray.length > 0 && Array.isArray(dataArray[0])) {
                  data = dataArray.flatMap((item: any) => item);
                } else {
                  data = dataArray;
                }
              } else if (Array.isArray(responseData.data)) {
                const dataArray = responseData.data;
                if (dataArray.length > 0 && Array.isArray(dataArray[0])) {
                  data = dataArray.flatMap((item: any) => item);
                } else {
                  data = dataArray;
                }
              } else if (Array.isArray(responseData.result)) {
                data = responseData.result;
              } else if (Array.isArray(responseData.records)) {
                data = responseData.records;
              } else if (responseData.value && Array.isArray(responseData.value)) {
                data = responseData.value;
              }
            }
            
            if (Array.isArray(data) && data.length > 0) {
              const normalize = (row: any) =>
                JSON.stringify(Object.keys(row).sort().reduce((acc: any, k: string) => {
                  acc[k] = row[k] == null ? '' : String(row[k]).trim();
                  return acc;
                }, {}));
              data = data.filter((row: any, index: number, arr: any[]) => {
                const key = normalize(row);
                return arr.findIndex((r: any) => normalize(r) === key) === index;
              });

              const headers = Object.keys(data[0]);
              let headerRowIndex = 0;
              const columnMap: { [key: string]: number } = {};

              for (let rowNum = 1; rowNum <= Math.min(5, worksheet.rowCount); rowNum++) {
                const row = worksheet.getRow(rowNum);
                const tempMap: { [key: string]: number } = {};
                let matchCount = 0;

                row.eachCell((cell: any, colNumber: number) => {
                  const cellValue = cell.value?.toString()?.trim();
                  if (cellValue) {
                    const matchedHeader = headers.find(h => h.toLowerCase() === cellValue.toLowerCase());
                    if (matchedHeader) {
                      tempMap[matchedHeader] = colNumber;
                      matchCount++;
                    }
                  }
                });

                if (matchCount > 0) {
                  headerRowIndex = rowNum;
                  Object.assign(columnMap, tempMap);
                  break;
                }
              }
              if (headerRowIndex === 0) {
                headerRowIndex = 1;
                headers.forEach((header, index) => {
                  columnMap[header] = index + 1;
                  worksheet.getRow(1).getCell(index + 1).value = header;
                });
                worksheet.getRow(1).font = { bold: true };
              }

              // Clear existing data rows from template to avoid duplicates
              const totalRows = worksheet.rowCount;
              if (totalRows > headerRowIndex) {
                worksheet.spliceRows(headerRowIndex + 1, totalRows - headerRowIndex);
              }

              const startRow = headerRowIndex + 1;
              data.forEach((row: any, rowIndex: number) => {
                const excelRow = worksheet.getRow(startRow + rowIndex);
                headers.forEach(header => {
                  const colNum = columnMap[header];
                  if (colNum) {
                    excelRow.getCell(colNum).value = row[header] ?? '';
                  }
                });
                excelRow.commit();
              });
              const buffer = await workbook.xlsx.writeBuffer();
            FileSaver.saveAs(new Blob([buffer]), `${fileName}.xlsx`);
            } else {
              this.alertService.error('No data found for the selected date range.');
            }
          },
          error: (err: any) => {
            this.alertService.error('Failed to download report template. Please try again.');
          }
        });
      },
      error: (err: any) => {
        this.alertService.error('Failed to generate report. Please check the date range and try again.');
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.accordion) {
        const originalToggle = this.accordion.toggle.bind(this.accordion);
        this.accordion.toggle = () => {
          originalToggle();
          if (this.accordion?.isOpen) {
            this.refreshGridLayout();
          }
        };
        this.accordion.isOpen = true;
      }
      if (this.reportGrid?.dataGrid?.instance) {
        this.reportGrid.dataGrid.instance.option('cellHintEnabled', false);
      }
      this.cdr.detectChanges();
    });
  }

  private disableCellHint(): void {
    if (this.reportGrid?.dataGrid?.instance) {
      this.reportGrid.dataGrid.instance.option('cellHintEnabled', false);
    }
  }

  private refreshGridLayout(): void {
    requestAnimationFrame(() => {
      this.reportGrid?.dataGrid?.instance?.updateDimensions();
      this.reportGrid?.dataGrid?.instance?.repaint();
    });
  }
}
